import { BriefingGrade, BriefingItemDto, KeyMetricDto } from '../../types/opsBriefing';
import { CoachAnalysis, pickCoachesToAct } from './coachAnalysis';
import { firstPassOfItem, hasServerFirstPass } from './adapters';

/**
 * 오늘의 브리핑 — **표시 편성**.
 *
 * ─────────────────────────────────────────────────────────────
 * 생성은 서버가 합니다. 여기서 정하는 것은 "먼저 보여줄 다섯 건" 뿐입니다.
 * ─────────────────────────────────────────────────────────────
 * 서버 편성(briefingPolicy · composition)은 그날 **무엇을 브리핑에 올릴지**를
 * 정합니다. 이 파일은 그렇게 올라온 것 중 **무엇을 먼저 눈에 넣을지**를 정합니다.
 * 둘은 다른 층입니다 — 여기서 항목을 지우지 않고, 접을 뿐입니다.
 *
 * 왜 접는가.
 *   원장님이 아침에 여는 화면에 29건이 세로로 늘어서 있으면 어느 것도 안 읽힙니다.
 *   "급하게 챙길 일이 29건" 은 정보가 아니라 겁주는 숫자입니다.
 *   실제로 오늘 손이 가는 것은 서너 건이고, 나머지는 내일도 거기 있습니다.
 *
 * ⚠️ **여기서 만든 순서를 완료 기록(index)에 쓰면 안 됩니다.**
 *    완료 기록 API 는 `PATCH .../history/:date/items/:index` 이고, 그 index 는
 *    **서버가 준 items 배열의 위치**입니다. 그래서 이 파일이 만드는 슬롯은
 *    정렬된 뒤에도 원래 위치(`index`)를 그대로 들고 다닙니다.
 *    화면은 표시만 정렬하고, 서버로 보낼 때는 이 `index` 를 씁니다.
 */

/** 기본 화면에 세우는 건수 — 회원 4 + 강사 1 */
export const BRIEFING_FOCUS_COUNT = 5;

/**
 * 위험 순.
 *
 * 서버도 대체로 이 순으로 주지만, 화면이 "위험 순으로 보여준다"고 말하려면
 * 화면이 그 순서를 보장해야 합니다. 같은 등급 안에서는 **서버 순서를 그대로**
 * 지킵니다 — 우리가 모르는 서버의 우선순위(금액·시급성)를 뒤집지 않습니다.
 */
const GRADE_RANK: Record<BriefingGrade, number> = {
    DANGER: 0,
    WARNING: 1,
    CHANCE: 2,
    PRAISE: 3,
};

/**
 * 휴면·주의 항목의 자리 — **기회 뒤, 칭찬 앞.**
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 등급 순서에 예외를 두나
 * ─────────────────────────────────────────────────────────────
 * 서버가 휴면 항목을 `WARNING` 으로 만듭니다 (2026-08-27 백엔드 2차).
 * **주의 항목도 같은 `WARNING` 입니다** (2026-09-01 백엔드 4차) — 그래서 같은
 * 예외를 탑니다. 안 태우면 주의 카드가 `WARNING(1)` 로 기회를 밀어내,
 * 2주 공백인 회원 때문에 만료 임박 회원이 접히는 날이 생깁니다.
 * 등급을 그대로 따르면 `WARNING(1) < CHANCE(2)` 라서 휴면이 재등록 기회보다
 * 앞에 섭니다. 기본 노출이 5건이라 **위험 4건 + 휴면 2건인 날 기회 카드가
 * 통째로 「더 보기」로 접힙니다.**
 *
 * 그건 순서 문제가 아니라 돈 문제입니다. 휴면은 62일 안 나온 회원이라
 * 오늘 전화한다고 오늘 오지 않지만, 재등록 기회는 **만료일이 있는 시한부**입니다.
 * 접혀서 못 본 날이 쌓이면 그대로 만료됩니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 등급을 `CHANCE` 로 바꾸는 방법은 쓰면 안 됩니다
 * ─────────────────────────────────────────────────────────────
 * 순서는 풀리지만 `briefingValue.valueKindOfGrade` 가 `CHANCE → GAIN` 으로 읽어,
 * 두 달 넘게 안 나온 회원에게 **「재등록 예상 ₩X」라는 없는 매출**을 찍습니다.
 * 서버도 같은 이유로 `WARNING` 을 골랐습니다 (백엔드 주석에 근거가 있습니다).
 * 그래서 **등급은 그대로 두고 정렬에만 예외**를 둡니다.
 *
 * 칭찬(3)보다는 앞입니다 — 휴면은 그래도 오늘 손댈 수 있는 일이고,
 * 칭찬은 읽고 넘어가는 줄입니다.
 */
const DORMANT_RANK = 2.5;

/**
 * 휴면 항목인가 — **`itemId` 접두사로만 판정합니다.**
 *
 * 서버가 `dormant-{memberId}` / `pace-{memberId}` 로 접두사를 갈라 둔 것이
 * 유일한 표식입니다(등급은 `WARNING` 이라 당일 취소·페이스 항목과 안 갈립니다).
 * 백엔드가 접두사를 "판정 종류가 보이는 편이 낫다"는 이유로 **의도적으로**
 * 남겼습니다 — 지난 리포트를 다시 열었을 때 id 만으로 읽히게 하려는 것입니다.
 *
 * ⚠️ 서버가 전용 필드(`verdict` 등)를 카드에 실어 주면 그쪽으로 옮기십시오.
 *    접두사는 계약이 아니라 관례라, 여기 한 곳에서만 읽어 옮기기 쉽게 둡니다.
 */
export const isDormantItem = (item: Pick<BriefingItemDto, 'itemId'>): boolean =>
    item.itemId.startsWith('dormant-');

/**
 * 주의 항목인가 — `watch-{memberId}` (2026-09-01 백엔드 4차).
 *
 * 「주의」는 안정 궤도 문턱을 못 넘은 회원입니다. 위험(이미 떠나는 중)도 아니고
 * 휴면(두 달 공백)도 아닌, **아직 괜찮지만 안심할 단계는 아닌** 자리입니다.
 * 등급은 휴면과 같은 `WARNING` 이라 등급만으로는 못 가릅니다 — 접두사가 유일한 표식입니다.
 */
export const isWatchItem = (item: Pick<BriefingItemDto, 'itemId'>): boolean =>
    item.itemId.startsWith('watch-');

/**
 * 🔴 **기회 뒤로 내려보낼 항목인가** — 정렬 전용 판정입니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 `isDormantItem` 을 그냥 넓히지 않았나
 * ─────────────────────────────────────────────────────────────
 * 정렬만 보면 「휴면 판정에 `watch-` 를 더한다」가 가장 짧습니다. 그런데
 * `isDormantItem` 은 **「휴면」 배지**도 함께 답니다(`BriefingCard`). 넓히면
 * 주의 카드에 「휴면」이 붙어, 2주 공백인 회원이 **두 달 안 나온 사람**으로
 * 읽힙니다 — 원장님이 거는 전화의 첫 마디가 달라집니다.
 *
 * 그래서 **묻는 질문을 갈랐습니다.**
 *   · `isDormantItem`  「이 카드가 휴면인가」   → 배지·문구
 *   · `ranksAfterChance` 「기회 뒤에 세울 것인가」 → 정렬
 *
 * 🔴 **서버와 같은 규칙이어야 합니다.** 서버도 4차에서 같은 정렬을 재현합니다
 *    (`opsBriefingPolicy` — dormant·watch 를 DORMANT_RANK 로 내림).
 *    규칙이 두 벌로 갈리면 **회전이 엉뚱한 회원에게 걸립니다** — 서버가 올린
 *    순서와 화면이 세운 순서가 달라, 원장이 누른 카드와 완료로 기록되는 항목이
 *    어긋납니다. 한쪽을 고치면 반드시 다른 쪽도 고치십시오.
 */
export const ranksAfterChance = (item: Pick<BriefingItemDto, 'itemId'>): boolean =>
    isDormantItem(item) || isWatchItem(item);

/**
 * 브리핑 항목 하나 — **서버 배열에서의 위치를 달고 다닙니다.**
 * 이 index 없이 정렬하면 완료 처리가 엉뚱한 항목에 걸립니다 (파일 상단 경고 참고).
 */
export type BriefingItemSlot = {
    kind: 'ITEM';
    item: BriefingItemDto;
    /** 서버가 준 items 배열에서의 위치 */
    index: number;
};

/**
 * 강사 인사이트 한 건 — **브리핑 항목이 아닙니다.**
 *
 * 강사 현황 탭이 쓰는 데이터(getCoaches → coachAnalysis)에서 최상위 한 명을
 * 뽑아 요약 줄로만 편성합니다. 서버 브리핑 문서에는 이 건이 없으므로
 * `itemId` 도 없고, 그래서 **가리기·완료 기록을 걸 수 없습니다.**
 * 눌렀을 때 열리는 것도 브리핑 카드가 아니라 강사 성과 리포트(CoachReportSheet)입니다.
 */
export type CoachInsightSlot = {
    kind: 'COACH';
    analysis: CoachAnalysis;
};

/**
 * 세팅·정착 과제 한 건 — **서버 브리핑 문서에 없는 웹 합성 카드입니다.**
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 브리핑 다섯 건 안에 넣나 (2026-08-28 대표 지시)
 * ─────────────────────────────────────────────────────────────
 * 세팅이 안 끝난 센터에서는 「회원 앱 설치 151명」이 그날 원장님이 할 **가장
 * 중요한 일**인데, 지금은 별도 섹션에만 있어서 브리핑 다섯 건에는 회원 항목만
 * 올라옵니다. 정작 급한 일이 아래에 접혀 있는 셈입니다.
 *
 * ⚠️ **`itemId` 가 없습니다.** 서버 문서에 없는 카드라 완료 기록·가리기를
 *    걸 수 없습니다(강사 인사이트 줄과 같은 성격입니다). 그래서
 *      · 처리 체크·가리기 대상에서 빠지고
 *      · **진행률 분모에서도 빠집니다** (`actionableFocus` 주석)
 */
export type SetupTaskSlot = {
    kind: 'SETUP';
    /**
     * 이 과제의 **안정 식별자** — 체크·가리기 저장의 키다 (2026-09-01 QA29).
     *
     * `onboarding:{단계키}` / `settling:{coachId}`.
     * 🔴 **제목이나 배열 순번으로 만들면 안 된다.** 제목은 대상 수가 바뀌면
     *    같이 바뀌고(「회원 앱 설치 151명」→「150명」), 순번은 축 하나가
     *    끝나면 밀린다. 둘 다 **어제 체크한 것이 오늘 다른 과제에 붙는다.**
     */
    id: string;
    /** 화면이 구분할 수 있게 — 세팅인가 강사 정착인가 */
    task: 'ONBOARDING' | 'COACH_SETTLING';
    title: string;
    detail: string;
    /** 「누구에게」 — 있으면 화면이 이름까지 보여준다 */
    names: string[];
    /**
     * 남은 대상 수 — 회원 카드의 「대상 수치」 자리에 해당한다 (2026-09-01 QA25).
     *
     * `null` 이면 **못 잰 것**이다(0 과 다르다). 화면은 숫자를 그리지 않는다 —
     * 「0명 남았습니다」는 끝났다는 뜻인데, 못 잰 것을 그렇게 적으면 거짓말이 된다.
     */
    count?: number | null;
    /**
     * 그 수의 **단위** — 「강사」인가 「회원」인가 (2026-09-01 QA34).
     *
     * 🔴 카드에서 설명 문장을 뺐기 때문에 **이 필드가 숫자의 뜻을 진다.**
     *    「회원 배정 · 3명」은 회원 셋으로 읽히지만 실제로는 담당 회원이 없는
     *    **강사** 3명이다. 없으면 화면이 단위 없이 「3명」만 적는다.
     *    (원천은 `setupTasks.ONBOARDING_UNIT`)
     */
    unit?: string;
    /**
     * 한 줄 부연 — **제목이 다 말하지 못하는 과제에만** 붙는다 (2026-09-01 QA34).
     *
     * 🔴 세팅 축(ONBOARDING)에는 **안 붙인다.** 거기 설명문은 제목과 숫자를
     *    그대로 되풀이하는 문장이라(「센터 연결 수락」+「2명이 아직 앱에서
     *    수락하지 않았습니다」) 대표 지시로 뺐다.
     *
     * 강사 정착만 예외다. 「무엇을 하면 풀리는가」(remedy)는 제목에도 숫자에도
     * 없는 정보라, 지우면 카드가 할 말이 없어진다. 지우는 것이 목적이 아니라
     * **같은 말을 두 번 안 하는 것**이 목적이다.
     */
    note?: string;
};

/*
 * 🔴 `action`(「강사 현황에서 보기」) 필드는 **없앴다** (2026-09-01 QA34).
 *    라우트는 실재했지만 신규 센터에서는 그 탭도 비어 있어서, 눌러도 볼 게
 *    없었다. 되살리려면 `setupTasks.ts` 의 그 자리 주석을 먼저 읽어라.
 */

export type BriefingSlot = BriefingItemSlot | CoachInsightSlot | SetupTaskSlot;

/**
 * 자리 보충 규칙이 **사실상 정하는 센터 단계** (2026-09-02 QA36).
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 이 판정을 다른 데서 다시 만들지 마라
 * ─────────────────────────────────────────────────────────────
 * QA11 주석이 「이 계산이 사실상 신규/과도기/정착 3분류다」라고 적어 두고도
 * 값으로 내보내지 않았다. 그래서 강사별 온보딩 화면이 자기 판정을
 * **따로** 만들었고(「세팅 졸업 여부」), 두 판정이 갈렸다 —
 * 회원 448명·브리핑 15건짜리 정착 센터가 수강권 없는 회원 216명 때문에
 * 영원히 미졸업으로 읽혀 **매일 아침 신규 센터 화면**을 봤다.
 *
 * 같은 식을 두 벌 두면 조용히 낡는다. 계산은 여기 한 번만 하고,
 * 화면들은 **결과를 읽는다.**
 *
 * ⚠️ **화면에 「신규 센터」·「과도기」라고 적지 않는다** (결정 ④).
 *    원장에게 등급을 매기는 말이 된다. 내부 판정 전용이다.
 */
export type CenterStage =
    /** 회원 카드 0건 — 세울 것이 세팅뿐이라 과제가 화면을 채운다 */
    | 'NEW'
    /** 회원 카드 1~4건 — 회원이 본체, 모자란 자리만 과제가 보충한다 */
    | 'TRANSITION'
    /** 회원 카드가 다섯 자리를 채웠다 — 과제 카드는 0장 */
    | 'SETTLED';

export type BriefingComposition = {
    /**
     * 🔴 **오늘 챙길 일 — 회원 카드 다섯 건.** 합성 카드는 여기 안 들어간다.
     *
     * 세 화면(아침 팝업 · 오늘의 브리핑 · 지난 리포트)이 **같은 다섯 명**을
     * 말해야 한다. 합성 카드가 자리를 가져가면 화면마다 「5건」의 뜻이 갈린다.
     */
    focus: BriefingItemSlot[];
    /** 목록 **위** 별도 블록 (세팅·정착 과제). 다섯 자리와 무관하다 */
    setupTasks: SetupTaskSlot[];
    /** 목록 **아래** 별도 줄 (강사 인사이트). 다섯 자리와 무관하다 */
    coachInsight: CoachInsightSlot | null;
    /** "외 N건 더 보기" 로 접히는 것 (남은 미처리 + 이미 처리한 건) */
    rest: BriefingItemSlot[];
    /** 원장이 가린 것 — 목록에서 빠지되 되돌릴 수 있게 따로 들고 있는다 */
    dismissed: BriefingItemSlot[];
    /** 아직 처리하지 않은 브리핑 항목 수 (가린 것 제외) */
    pendingCount: number;
    /**
     * 회원 카드가 **못 채운 자리** 수 (`focusCount - focus.length`).
     *
     * 과제 카드·강사 카드가 몇 장까지 설 수 있는지의 분모다. 화면이 이 값을
     * 다시 계산하면 카드 수가 갈린다 — 읽어서 쓴다.
     */
    openSlots: number;
    /** 내부 판정 전용 센터 단계 — 화면에 문구로 적지 않는다 ([[CenterStage]]) */
    centerStage: CenterStage;
};

/**
 * 🔴 **진행률의 분모는 `focus.length`** — 오늘 챙길 일 다섯 건이다.
 *
 * 합성 카드가 focus 밖으로 나가면서 「처리할 수 있는 것만 골라내는」 함수가
 * 필요 없어졌다. focus 는 이제 전부 서버 항목이라 그대로 세면 된다.
 *
 * ⚠️ `items.length`(그날 전부)를 분모로 쓰면 안 된다. 머리말은 「5건 · 외 N건」
 *    인데 진행률만 「전체 30건」이면 같은 화면의 두 숫자가 다른 것을 센다.
 */

/**
 * 위험 순 세우기 — **오늘의 브리핑과 지난 리포트가 같이 쓰는 알맹이.**
 *
 * 두 화면은 다루는 자료가 다릅니다. 오늘은 서버 브리핑 항목(BriefingItemDto),
 * 지난 리포트는 그날 기록의 요약 줄(DayReportDto.highlights)입니다.
 * 그래도 **같은 날을 놓고 다른 다섯 건을 고르면 안 됩니다** — 원장님이
 * 8월 20일 화면에서 본 다섯 건과 그날의 아침 브리핑이 달라지기 때문입니다.
 *
 * 그래서 "무엇을 먼저 세우나" 규칙만 여기에 두고, 자료의 모양은 함수 인자로 받습니다.
 *
 * ⚠️ 원래 위치(index)를 반드시 달고 다닙니다. 두 화면의 완료 기록 API 가 모두
 *    **배열 위치**로 항목을 찾습니다(파일 상단 경고 참고).
 */
export type RankedSlot<T> = { entry: T; index: number };

/**
 * @param firstPassOf 첫 수강권 회원인가 — **같은 등급 안에서만** 앞으로 당깁니다.
 *
 * 2026-08-27 대표 결정 ③. 첫 수강권은 **카드가 뜨는 이유가 아닙니다** —
 * 카드는 기존 문제 신호(공백·페이스·골든타임·당일 취소)로만 뜨고, 순번은
 * 「같은 문제면 먼저 보여준다」에만 씁니다.
 *
 * 🔴 **등급 순서를 뒤집지 않습니다.** 첫 수강권 WARNING 이 DANGER 를 앞지르면
 *    화면이 "위험 순으로 보여준다"는 약속을 깨고, 이탈률 근거(첫 수강권 47.1% ·
 *    2번째 46.6%)는 그런 역전을 지지하지 않습니다 — 첫 두 수강권이 거의 같습니다.
 *    안 넘기면 예전과 **완전히 같은 순서**입니다 (서버가 원본을 아직 안 보냅니다).
 */
export const rankByGrade = <T>(
    list: T[],
    gradeOf: (entry: T) => BriefingGrade,
    firstPassOf?: (entry: T) => boolean,
    dormantOf?: (entry: T) => boolean
): RankedSlot<T>[] => {
    /*
     * 휴면·주의만 등급 순서에서 예외로 내려간다 (DORMANT_RANK 주석).
     * 안 넘기면 예전과 **완전히 같은 순서**입니다 — 지난 리포트가 그 경로입니다.
     */
    const rankOf = (entry: T): number =>
        dormantOf?.(entry) ? DORMANT_RANK : GRADE_RANK[gradeOf(entry)];

    return list
        .map((entry, index): RankedSlot<T> => ({ entry, index }))
        .sort((a, b) => {
            const rank = rankOf(a.entry) - rankOf(b.entry);
            if (rank !== 0) return rank;
            /*
             * 같은 등급 안에서만 첫 수강권을 앞으로.
             * 순번을 모르는 항목(서버가 원본을 안 보냄·재등록 유입·미분류)은
             * false 라 제자리에 있습니다 — 모를 때 당기지 않습니다.
             */
            if (firstPassOf) {
                const fp = Number(firstPassOf(b.entry)) - Number(firstPassOf(a.entry));
                if (fp !== 0) return fp;
            }
            /* 그다음은 서버 순서 — 우리가 모르는 서버의 우선순위를 뒤집지 않는다 */
            return a.index - b.index;
        });
};

/**
 * 아직 안 한 것을 앞으로, 이미 한 것을 뒤로 — 그 다음 앞에서 focusCount 만큼 끊는다.
 *
 * 처리한 건을 무조건 접어 버리면 다 끝낸 날에는 기본 화면이 통째로 비고,
 * 잘못 누른 한 건을 되돌리려면 "외 N건 더 보기"부터 찾아야 합니다.
 * 자리가 남으면 처리한 건이 그 자리를 채웁니다 — 사라지지 않고 접힙니다.
 */
export const splitFocus = <T>(ranked: T[], focusCount: number): { focus: T[]; rest: T[] } => {
    const cut = Math.max(0, focusCount);
    return { focus: ranked.slice(0, cut), rest: ranked.slice(cut) };
};

/**
 * 서버 배열 위치를 붙인 뒤 위험 순으로 세운다.
 *
 * 세대에 따라 등급 안의 규칙이 다릅니다 (아래 본문 주석).
 *   · v8+ (서버가 `isFirstPass` 를 싣는 문서) → **서버 순서 그대로**
 *   · v7 이하 (옛 문서·지난 리포트)          → 예전처럼 웹이 첫 수강권을 당김
 *
 * ⚠️ 순서를 바꿔도 `index` 는 **서버 배열 위치 그대로**입니다. 완료 기록 API 가
 *    그 위치로 항목을 찾습니다 (파일 상단 경고).
 */
export const rankItems = (items: BriefingItemDto[]): BriefingItemSlot[] => {
    /*
     * ─────────────────────────────────────────────────────────────
     * 🔴 첫 수강권 가중은 **서버가 하는 일이 됐습니다** (백엔드 3.5, v8)
     * ─────────────────────────────────────────────────────────────
     * 서버가 등급 안을 「최근성 → 첫 수강권 → 긴급도」로 이미 세워서 보냅니다.
     * 그런데 웹이 여기서 첫 수강권을 다시 앞으로 당기면 **서버가 건 회전이
     * 그 회원에게만 안 걸립니다** — 첫 수강권 회원이 며칠이고 1번 자리에
     * 고정되고, 서버가 회전시키려던 나머지가 계속 밀립니다.
     * 회전을 넣은 이유가 「매일 같은 이름이 1순위를 점거하는 것」을 막는
     * 것이었으니, 여기서 당기면 그 수정이 통째로 무효가 됩니다.
     *
     * ⚠️ **무조건 끄지는 않습니다.** 옛 문서(v7 이하)에는 서버 순서가 없고
     *    `isFirstPass` 키도 없습니다. 오늘 아침 생성분도 아직 그럴 수 있고,
     *    지난 리포트를 열면 확실히 그렇습니다. 거기서 가중을 빼면
     *    **과거의 첫 수강권 우선이 소급해서 사라집니다.**
     *    그래서 키의 유무로 세대를 감지해 갈래를 나눕니다.
     *
     * ⚠️ 등급 순서(GRADE_RANK)와 휴면 예외(DORMANT_RANK)는 **양쪽 다 그대로**입니다.
     *    서버도 같은 등급 순서를 씁니다 — 여기서 뒤집으면 두 층이 싸웁니다.
     */
    const serverOrdered = hasServerFirstPass(items);

    return rankByGrade(
        items,
        (i) => i.grade,
        /*
         * v8+ 면 서버 순서를 그대로 둔다(등급 → 서버 index).
         * 옛 문서면 예전처럼 웹이 당긴다.
         */
        serverOrdered ? undefined : (i) => firstPassOfItem(i),
        /* 휴면·주의는 기회 뒤로 — 기회 카드가 접히지 않게 (DORMANT_RANK 주석) */
        (i) => ranksAfterChance(i)
    ).map(({ entry, index }): BriefingItemSlot => ({ kind: 'ITEM', item: entry, index }));
};

/**
 * 오늘 편성할 강사 한 명.
 *
 * 판정은 강사 현황 탭과 **같은 함수**(pickCoachesToAct)를 씁니다.
 * 두 화면이 다른 기준으로 "오늘 챙길 강사"를 고르면 원장님은 어느 쪽을 믿어야
 * 할지 알 수 없습니다. 집중 관리(CRITICAL) → 점검 필요(WATCH) 순이고,
 * 같은 등급이면 담당 회원 미사용 선수금이 큰 사람이 먼저입니다.
 *
 * ⚠️ 서버 브리핑에 이미 그 강사 항목이 올라와 있으면 **서버 것을 씁니다.**
 *    서버 항목은 itemId 가 있어 가리기·완료 기록이 되고, 판단 문장도 그날 기준입니다.
 *    둘 다 그리면 같은 강사가 한 화면에 두 줄로 뜹니다.
 */
export const pickCoachInsight = (
    analyses: CoachAnalysis[],
    items: BriefingItemDto[]
): CoachAnalysis | null => {
    const top = pickCoachesToAct(analyses, 1)[0] ?? null;
    if (!top) return null;
    const alreadyOnBriefing = items.some(
        (it) => it.subjectType === 'COACH' && it.subjectId === top.coach.coachId
    );
    return alreadyOnBriefing ? null : top;
};

/**
 * 오늘 화면에 세울 것을 정한다.
 *
 * 가리기 백필이 여기서 저절로 일어납니다 — 가린 항목은 `DISMISSED` 가 되어
 * 미처리 목록에서 빠지고, 그 다음 위험 순위가 빈 자리를 채웁니다.
 * 화면이 따로 "한 칸 당겨오는" 코드를 갖지 않습니다. 그런 코드는 새로고침 뒤
 * 서버 상태와 어긋나는 순간 목록이 조용히 달라집니다.
 */
/**
 * 세팅·정착 카드가 가져갈 수 있는 **최대 자리** (2026-08-28 대표 지시).
 *
 * 둘 다 있어도 두 장까지입니다. 다섯 자리 중 셋이 과제 카드가 되면
 * 그날의 회원 브리핑이 사실상 사라집니다 — 이 화면의 본체는 회원입니다.
 */
export const MAX_SETUP_SLOTS = 2;

/**
 * 회원 카드가 내려갈 수 있는 **하한**.
 *
 * 세팅 카드가 자리를 가져가도 회원 카드는 세 장 아래로 안 내려갑니다.
 * 신규 센터라도 이탈 위험 회원은 그날 챙겨야 하고, 과제 카드에 밀려
 * 위험이 「더 보기」로 접히면 그건 이 화면이 하려던 일과 반대입니다.
 */
export const MIN_MEMBER_SLOTS = 3;

export const composeBriefing = (
    items: BriefingItemDto[],
    coachInsight: CoachAnalysis | null = null,
    focusCount: number = BRIEFING_FOCUS_COUNT,
    setupTasks: SetupTaskSlot[] = []
): BriefingComposition => {
    const ranked = rankItems(items);

    const pending = ranked.filter((s) => s.item.status === 'PENDING');
    const dismissed = ranked.filter((s) => s.item.status === 'DISMISSED');

    /*
     * ═══════════════════════════════════════════════════════════
     * 🔴 「오늘 챙길 일 = 회원 카드 다섯 건」 — 하루 고정 (2026-08-28 결정)
     * ═══════════════════════════════════════════════════════════
     * 합성 카드(세팅 과제·강사 인사이트)는 **이 다섯 자리를 쓰지 않는다.**
     * 목록 위·아래 별도 블록으로 나간다.
     *
     * 하루 동안 자리를 가져가게 뒀더니 화면마다 「5건」의 뜻이 갈렸다 —
     * 지난 리포트는 회원 5건, 오늘의 브리핑은 회원 3~4건 + 합성 카드,
     * 진행률은 또 다른 수. 세 곳이 같은 날을 두고 다른 숫자를 말했다.
     * 이제 셋 다 **서버 회원 항목 상위 다섯**이라는 한 모집단을 본다.
     */
    const visible = ranked.filter((s) => s.item.status !== 'DISMISSED');

    /*
     * ─────────────────────────────────────────────────────────────
     * 🔴 완료(ACTED)는 **그 자리에 남는다.** 리필하지 않는다
     * ─────────────────────────────────────────────────────────────
     * 예전에는 `splitFocus` 가 완료 건을 뒤로 밀어서, 한 건을 처리하면
     * 여섯 번째가 그 자리로 올라왔다. 그래서 아무리 처리해도 화면은 늘
     * 미완료 다섯 건이었고 **진행률이 영원히 안 찼다** — 「오늘 할 일이
     * 끝났다」는 순간이 오지 않는다.
     *
     * 「했다」와 「안 볼래」는 다른 말이다.
     *   · ACTED    → 자리를 **채운** 것이다. 완료 표시로 그 자리에 남는다.
     *   · DISMISSED → 자리를 **비운** 것이다. 다음 순위가 올라온다(위 filter).
     * 그래서 리필은 가리기에만 걸린다.
     */
    const { focus, rest } = splitFocus(visible, focusCount);

    const coachSlots: CoachInsightSlot[] = coachInsight
        ? [{ kind: 'COACH', analysis: coachInsight }]
        : [];

    /*
     * ═══════════════════════════════════════════════════════════
     * 🔴 과제 카드는 **빈 자리를 채우는 것**이다 (2026-08-31 QA11)
     * ═══════════════════════════════════════════════════════════
     * 대표 확정: 세팅·정착 과제 카드는 **회원 위험 카드가 없는 신규 센터의
     * 대체재**다. 회원 카드가 다섯 건 꽉 찬 센터 위에 얹는 것이 아니다.
     *
     * 예전 규칙(온보딩 미졸업이면 무조건 표시)은 개설 917일짜리 미졸업 센터에도
     * 과제 카드를 얹었다. 그 센터는 챙길 회원이 이미 다섯 건 있고, 과제 카드는
     * 그 위에서 자리만 차지했다.
     *
     * 그래서 **모자란 자리만큼만** 보충한다.
     *   회원 5건 → 남는 자리 0 → 과제 0장
     *   회원 3건 → 남는 자리 2 → 과제 최대 2장
     *   회원 0건 → 남는 자리 5 → 과제가 화면을 채운다 (상한 2장)
     *
     * ⚠️ 이 계산이 사실상 **신규 / 과도기 / 정착** 3분류다.
     *      신규   = 회원 항목이 거의 없어 과제가 자리를 다 채운다
     *      과도기 = 일부만 보충된다 (회원도 있고 과제도 있다)
     *      정착   = 회원 다섯 건으로 충족되어 과제가 사라진다
     *    **내부 판정으로만 쓴다.** 화면에 「신규 센터」·「과도기」라고 적지
     *    않는다(결정 ④) — 원장에게 등급을 매기는 말이 되기 때문이다.
     *    분류가 필요하면 이 수를 보면 되고, 화면에는 할 일만 적는다.
     *
     * ⚠️ 남는 자리는 **`focus.length` 기준**이다. `visible.length` 로 재면
     *    여섯 번째 이후가 있는 날 남는 자리가 음수가 되고, 반대로 가린 건까지
     *    세면 이미 비운 자리를 두 번 채우게 된다.
     */
    const openSlots = Math.max(0, focusCount - focus.length);

    /*
     * ─────────────────────────────────────────────────────────────
     * 🔴 **회원 카드가 하나도 없으면 상한을 풀고 전부 세운다** (2026-09-01 QA29)
     * ─────────────────────────────────────────────────────────────
     * 상한 2장은 「회원 브리핑이 과제 카드에 밀리면 안 된다」는 규칙이다
     * (MAX_SETUP_SLOTS 주석). 그런데 **회원 카드가 0건인 신규 센터**에는
     * 밀릴 회원 브리핑 자체가 없다. 거기서 2장만 세우면 원장님은 나머지
     * 다섯 축을 아예 못 보고, 그 화면이 그 센터가 가진 전부다.
     *
     * 과도기(회원 카드 1~4건)는 **기존 규칙 그대로** 둔다 — 회원 카드가
     * 하나라도 있으면 그게 본체이고, 과제는 빈 자리만 채운다.
     */
    const setupLimit = focus.length === 0 ? setupTasks.length : Math.min(openSlots, MAX_SETUP_SLOTS);

    return {
        focus,
        openSlots,
        /*
         * 🔴 단계 판정은 **여기 한 번**. 강사별 온보딩 화면·아침 팝업이
         *    각자 「세팅 졸업 여부」로 다시 판정하다가 정착 센터에
         *    신규 센터 화면을 띄웠다 (2026-09-02 QA36).
         */
        centerStage: focus.length === 0 ? 'NEW' : openSlots === 0 ? 'SETTLED' : 'TRANSITION',
        /* 목록 **위** 별도 블록 — 회원이 못 채운 자리만큼만 (위 주석) */
        setupTasks: setupTasks.slice(0, setupLimit),
        /* 목록 **아래** 별도 줄 — 원래 인사이트 줄 위치 */
        coachInsight: coachSlots[0] ?? null,
        rest,
        dismissed,
        pendingCount: pending.length,
    };
};

/* ═══════════════════════════════════════════════════════════
   지난 리포트 — 날짜 상세의 항목 목록
   ═══════════════════════════════════════════════════════════ */

/**
 * 지난 리포트에서 그날의 항목을 세우는 방법.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 지난 리포트도 다섯 건인가
 * ─────────────────────────────────────────────────────────────
 * 날짜를 누르면 그날 올라온 것이 **서른 줄 전부** 늘어섰습니다.
 * 오늘의 브리핑은 이미 다섯 건으로 접었는데 지난 리포트만 전부 펼쳐 두면,
 * 같은 8월 20일을 두 화면에서 보고 "어느 쪽이 그날 실제였나" 를 알 수 없습니다.
 *
 * 편성 규칙(rankByGrade · splitFocus)은 오늘의 브리핑과 **같은 함수**를 씁니다.
 * 규칙을 각자 적으면 두 화면의 다섯 건이 언젠가 반드시 갈립니다.
 *
 * ⚠️ 오늘의 브리핑은 다섯 자리 중 하나를 **강사 인사이트**가 가져가므로 회원 항목이
 *    넷입니다. 지난 리포트에는 그 줄이 없습니다 — 그날 기록(highlights)에 강사
 *    인사이트가 남지 않기 때문입니다(브리핑 항목이 아니라 강사 현황 탭 파생값이라
 *    itemId 도 없습니다). 그래서 여기서는 항목 다섯 건을 세웁니다.
 *    **접는 규칙과 순서가 같다**는 것이 이 공유의 요점이고, 자리 배분은 화면마다 다릅니다.
 *
 * ⚠️ 완료 기록 API 는 `PATCH .../history/:date/items/:index` 이고 그 index 는
 *    **서버가 준 highlights 배열의 위치**입니다. 정렬한 뒤에도 `index` 를 그대로
 *    들고 다니는 이유가 이것입니다. 화면 순서를 index 로 쓰면 엉뚱한 항목이 완료됩니다.
 *
 * ⚠️ **첫 수강권 가중은 여기 없습니다.** 그날 기록(highlights)에는 수강권 원본이
 *    실리지 않아 순번을 셀 수가 없습니다. 오늘의 브리핑에 가중이 들어온 뒤로는
 *    같은 날을 두고 **같은 등급 안의 순서가 갈릴 수 있습니다.**
 *    (등급 순서와 접는 규칙은 그대로 같습니다)
 *    되살리려면 서버가 `DayReportDto.highlights[]` 에 순번 원본이나 첫 수강권
 *    플래그를 같이 실어야 합니다 — 백엔드에 넘길 항목입니다.
 *
 * ✅ **휴면 예외는 이제 여기에도 있습니다** (2026-08-27 백엔드 3차).
 *    `DayReportDto.highlights[].itemId` 가 오기 시작해서 접두사로 휴면을 가려낼 수
 *    있게 됐습니다. 그전에는 지난 리포트만 휴면을 기회보다 앞에 세워서, 같은 날을
 *    두 화면에서 볼 때 다섯 건이 갈렸습니다. 이제 두 화면이 같은 순서입니다.
 *    (`itemId` 가 없는 응답이면 예전처럼 휴면 정렬만 생략됩니다)
 */
export type HighlightLike = {
    grade: BriefingGrade;
    done: boolean;
    /** 서버 3차부터 옵니다. 없으면 휴면 정렬만 생략됩니다 */
    itemId?: string;
};

export const composeHighlights = <T extends HighlightLike>(
    highlights: T[],
    focusCount: number = BRIEFING_FOCUS_COUNT
): { focus: RankedSlot<T>[]; rest: RankedSlot<T>[] } =>
    splitFocus(
        rankByGrade(
            highlights,
            (h) => h.grade,
            /*
             * 🔴 첫 수강권 가중은 **여기 넘기지 않습니다.** 위 주석 참고 —
             *    그날 기록에는 수강권 원본이 없어 순번을 셀 수 없습니다.
             *    (대표 판단 대기 건이기도 합니다 — 임의로 켜지 마세요)
             */
            undefined,
            /* 휴면·주의는 기회 뒤로 — 오늘의 브리핑과 같은 규칙 (DORMANT_RANK) */
            (h) => h.itemId != null && ranksAfterChance({ itemId: h.itemId })
        ),
        /*
         * 🔴 완료(done) 건도 **자리를 지킨다.** 오늘의 브리핑과 같은 규칙이다.
         *    예전에는 완료를 뒤로 밀어서, 같은 날을 두 화면에서 볼 때
         *    다섯 건의 구성이 갈릴 수 있었다.
         */
        focusCount
    );

/**
 * 🔴 하루 기록을 **focus 다섯 건 기준**으로 다시 센다 (2026-08-31 QA10).
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 서버 총계를 그대로 쓰면 안 되나
 * ─────────────────────────────────────────────────────────────
 * 지난 리포트는 그날의 **다섯 건만** 보여준다(대표 결정). 그런데 달력 점과
 * 「N건 중 M건 실행」은 서버 총계(`items`·`acted`)를 그대로 썼다. 그래서
 * 목록은 5줄인데 달력에는 점이 8개, 머리말은 「8건 중 0건 실행」이 됐다.
 * 같은 날을 두고 화면이 두 가지 수를 말한 것이다.
 *
 * 이 함수는 그 하루를 목록과 **같은 모집단**으로 바꾼다. 한 번 통과시키면
 * 달력·주간 줄·월 실행률(`summarizeMonth` 가 이 값을 합산한다)이 전부 따라온다.
 *
 * ⚠️ **세부 기록이 없는 날은 손대지 않는다.** 옛 문서에는 `highlights` 가
 *    비어 있어 그날 무엇이 다섯 건이었는지 알 방법이 없다. 여기서 5 로 깎으면
 *    모르는 것을 아는 척하는 것이다 — 서버 총계를 그대로 두고, 화면은 이미
 *    그런 날에 「세부 항목 기록은 남아 있지 않습니다」라고 밝힌다.
 *
 * ⚠️ 「자리에 밀려 애초에 안 올라온 건」을 실행률 분모에서 빼던 기존 각주와
 *    같은 방향이다. 이제는 **올라왔지만 다섯 건 밖으로 밀린 건**도 함께 빠진다 —
 *    둘 다 「그날 원장 화면에 없던 것」이고, 없던 것을 안 했다고 세지 않는다.
 */
export const toFocusDay = <T extends { items: number; acted: number; highlights: HighlightLike[] }>(
    day: T,
    focusCount: number = BRIEFING_FOCUS_COUNT
): T => {
    if (!day.highlights || day.highlights.length === 0) return day;
    const { focus } = composeHighlights(day.highlights, focusCount);
    return {
        ...day,
        items: focus.length,
        acted: focus.filter((s) => s.entry.done).length,
    };
};

/**
 * 이전 구간이 회원 등록 이후를 덮는가 — **판정은 여기 한 곳에서만.**
 *
 * 서버가 키를 안 보내면 `true`(기존 동작)입니다. 없는 것을 "등록 전"으로 읽으면
 * 지금까지 멀쩡히 보이던 카드가 전부 '등록 전' 이 됩니다.
 * `false` 로 **명시했을 때만** 등록 전으로 봅니다.
 */
export const keyMetricBeforeCovered = (metric: Pick<KeyMetricDto, 'beforeCovered' | 'paceBeforeCovered'>): boolean =>
    metric.beforeCovered !== false && metric.paceBeforeCovered !== false;

/**
 * 이전 구간 표기.
 *
 * 등록 전이면 "0회" 대신 '등록 전' 이라고 적습니다. 0 은 "재봤더니 0회"이고
 * 등록 전은 "잴 구간이 아니었다"입니다 — 이 저장소가 null 과 0 을 가르는 규칙과 같습니다.
 */
export const BEFORE_UNCOVERED_TEXT = '등록 전';

export const beforeValueOf = (metric: KeyMetricDto): string =>
    keyMetricBeforeCovered(metric) ? metric.beforeValue : BEFORE_UNCOVERED_TEXT;

/**
 * 변화율을 말할 수 있는가.
 *
 * 등록 전 구간과 비교한 "▼100% 감소" 는 근거가 없는 숫자입니다.
 * 비교가 성립하지 않으면 배지를 만들지 않고 '비교 없음' 이라고 적습니다.
 */
export const BEFORE_UNCOVERED_DELTA_TEXT = '비교 없음';

export const canCompareDelta = (metric: KeyMetricDto): boolean => keyMetricBeforeCovered(metric);
